// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the timeline surface
//  ----------------------------------------------------------------------------
//  One component, two screens. The Workshop's timeline view and the database's
//  both hand it the same shape — rows of `{ id, name, start, target }` — and get
//  back a frozen rail, a scrolling canvas, a month band, a today line, and bars
//  you can take hold of. The arithmetic underneath is md-timeline.ts, which
//  knows days and pixels and nothing about any of this.
//
//  ── POINTER EVENTS, NOT HTML5 DRAG-AND-DROP ───────────────────────────────
//  Everything draggable in this app so far is `draggable` + `dragstart`, which
//  does not exist on touch. The database's own bars have that bug today: a
//  timeline that cannot be used on the iPad it was designed on is not a
//  timeline. Pointer events are one API for mouse, pen and finger, and the
//  keyboard equivalents below are not a consolation prize — every gesture here
//  has an arrow-key twin.
//
//  ── A GESTURE IS ONE WRITE ────────────────────────────────────────────────
//  The surface holds a DRAFT while the pointer is down and writes once, on
//  release. Writing per move would fill the undo history with sixty steps
//  nobody made and sync sixty documents for one decision.
//
//  ── RELEASING OFF THE CANVAS CANCELS ──────────────────────────────────────
//  A pointer dragged out of the window and let go there has not chosen a day.
//  Writing the last day it happened to cross would be the surface inventing an
//  answer to a question that was abandoned.
//
//  ── the label goes ABOVE the bar ──────────────────────────────────────────
//  Inside it, a two-day span at month zoom is 18 pixels of nothing. Above it,
//  every project reads at every zoom, which is the whole reason to have a
//  timeline rather than a list of dates.
//
//  ── ONE AXIS, AND THE NAME COLUMN FLOATS ON TOP OF IT ─────────────────────
//  The days run the WHOLE width of the surface, from its left edge, and the
//  frozen column of names is an overlay sitting on the first `rail` pixels of
//  them. The ruler is therefore continuous: scroll back and the months keep
//  going above the column instead of stopping at its edge, which is what makes
//  the header read as one ruler rather than two panes that happen to touch.
//
//  The cost is deliberate and paid twice: a bar can slide UNDER the column and
//  be hidden there (it gets the same off-screen stub a bar off the right edge
//  gets), and the column's background must be fully opaque or the bars ghost
//  through it. The alternative — a canvas that begins where the column ends —
//  is two coordinate systems, and it cannot draw the past above the names at
//  all.
//
//  Everything below therefore measures in ONE coordinate space (canvas x), and
//  the part of it somebody can actually SEE is `view` — the window minus the
//  column's own width. Stubs, the drag pill and every scroll-into-view answer
//  are stated in those terms.
// ════════════════════════════════════════════════════════════════════════════
const { useCallback: useCbT, useEffect: useEfT, useMemo: useMeT, useRef: useRefT,
  useState: useStT } = React;
const { ink: inkT, mdRgba: rgbaT, mdIsLight: isLightT } = window;

const TL_RAIL = 190;          // the frozen name column
const TL_RAIL_MIN = 118;      // and the least of it a name survives
const TL_RAIL_MAX = 420;      // and the most of it before it is a list, not a timeline
const TL_RAIL_STEP = 16;      // one press of the column's keyboard twin
const TL_NARROW = 560;        // below this the rail takes a share, not a size
const TL_ROW = 46;            // one project's band — 44px grips need the room
const TL_BAR = 17;            // the bar itself, with its name on the line above
const TL_HEAD = 42;           // month band + ruler
const TL_GRIP = 13;           // how wide an edge is to grab
const TL_EDGE = 48;           // how near the rim the pointer starts a pan
const TL_PAN = 26;            // and how far each nudge carries
const TL_GROW = 182;          // half a year of window, added when a scroll reaches the end
const TL_GROW_EDGE = 320;     // and how near the end counts as reaching it
const TL_FIT_PAD = 4;         // the breath a fitted column keeps past its longest name
const TL_FLICK_MIN = 0.06;    // px/ms under which a release was a stop, not a throw
const TL_FLICK_MAX = 4.5;     // and above which it was a slip of the hand
const TL_FLICK_DECAY = 0.94;  // how much of its speed a coast keeps each frame
const TL_FLICK_STOP = 0.02;   // and where a coast is close enough to stopped

const TL_ZOOM_LABEL = { day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter' };

/* A day, written the way a person says it — the same short form the Workshop's
   list uses, so the pill under the cursor and the column agree. */
function tlDayLabel(day) {
  if (typeof day !== 'string' || day.length < 10) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(day.slice(8, 10))} ${months[Number(day.slice(5, 7)) - 1] || ''}`;
}

function tlSpanLabel(start, target) {
  if (start && target && start !== target) return `${tlDayLabel(start)} – ${tlDayLabel(target)}`;
  return tlDayLabel(target || start);
}

/* Asked at the moment of the gesture rather than at mount: somebody who turns
   reduced motion on mid-session means it now, not next reload. */
function tlReducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function MdTimeline({ rows = [], todayKey, zoom = 'month', onZoom = null, rtl = false,
  accent = '#7C68E6', readOnly = false, resizable = true, onOpen = null, onWrite = null,
  rail: railFor = null, sections = null, railDrag = null,
  empty = 'Nothing here has days yet. Drag across a row to give one a span.' }) {
  const light = isLightT();

  /* ── one ladder of rungs ─────────────────────────────────────────────────
     Grouped, the rail is sections: `[{ id, label, mark, count, rows }]`, and a
     header takes a rung exactly like a row does. That one decision keeps every
     piece of TL_ROW arithmetic — the virtualization band, the bar y, the stub
     window — arithmetic, instead of a sum over two different heights. Sections
     do not fold: a collapse is a release of its own. Without sections (the
     database's path) the ladder is simply the rows. */
  const lanes = useMeT(() => (sections
    ? sections.reduce((out, section) => {
      out.push({ head: section });
      for (const row of section.rows) out.push({ row });
      return out;
    }, [])
    : rows.map((row) => ({ row }))), [sections, rows]);
  const laneRows = useMeT(() => (sections
    ? lanes.filter((lane) => lane.row).map((lane) => lane.row) : rows), [lanes, sections, rows]);
  const scroller = useRefT(null);
  const canvas = useRefT(null);
  const ruler = useRefT(null);
  const head = useRefT(null);
  const gesture = useRefT(null);
  const panTimer = useRefT(null);

  /* The draft. While the pointer is down this is what the bar draws from, so
     the span follows the hand without a single write leaving the component. */
  const [live, setLive] = useStT(null);
  /* `host` is the one thing measured — the scroller's own width. The rail and
     the usable canvas are DERIVED from it, so there is never a moment where a
     narrowed rail and a stale canvas width disagree about where the middle is. */
  const [port, setPort] = useStT({ left: 0, host: 0, top: 0, height: 0 });
  /* On a phone a 190px name column is half the screen and the days get what is
     left. Below the width where that stops being reasonable the rail takes a
     share instead of a fixed size, with a floor a name can still be read in.
     Every measurement below is in terms of THIS number, not the constant. */
  /* …but a fitted width beats a guessed one. 190 is a number about nobody's
     projects: it truncates "40 Days Takbīrat al-Iḥrām Jamāʿah Prayer" and
     wastes half itself on an account whose projects are called "Rent". The
     column is measured against the LONGEST name it actually holds, plus the
     chips hanging beside it — see `railFit` below for how, and why the names
     are measured in a canvas rather than in the DOM. */
  const [railFit, setRailFit] = useStT(null);
  const railAuto = railFit != null ? railFit : TL_RAIL;
  /* …until a hand says otherwise. A width somebody DRAGGED is a decision, and
     it outranks both — but only as far as the days can afford: a name column
     wide enough to swallow the canvas is not a preference, it is a broken
     screen. On a phone the same rule reads as the narrow case it always was:
     a share of the width, with a floor a name survives in. */
  const [railHeld, setRailHeld] = useStT(null);
  const railRoom = port.host > 0
    ? Math.max(TL_RAIL_MIN, Math.min(TL_RAIL_MAX,
      port.host < TL_NARROW ? Math.round(port.host * 0.38) : port.host - TL_RAIL_MIN))
    : TL_RAIL_MAX;
  const rail = Math.min(Math.max(railHeld == null ? railAuto : railHeld, TL_RAIL_MIN), railRoom);
  const seen = Math.max(0, port.host - rail);

  /* ── how far the window reaches ──────────────────────────────────────────
     The engine opens on two years of future and back to the first date anybody
     set. `grown` is what THIS surface has asked for on top of that, in days,
     because a scroll that arrives at the end of the window asks for more rather
     than stopping dead. */
  const [grown, setGrown] = useStT({ behind: 0, ahead: 0 });
  const growing = useRefT(false);
  const nudged = useRefT(0);

  /* ── the scale between the four names ────────────────────────────────────
     A pinch is continuous and the chips are four steps, so the surface keeps a
     stretch of its own on top of whatever zoom it was handed. Like the column's
     width and unlike the zoom itself, this is the surface's business: a factor
     nobody named is not a decision worth writing into a saved view, and the
     name — which IS written — follows the scale as it crosses. */
  const [stretch, setStretch] = useStT(1);
  /* Which zoom the pinch itself asked for. A zoom that arrives from anywhere
     else (a chip, a saved view, another screen) is a fresh named scale and
     starts unstretched; the one the pinch just asked for is already at the
     scale the fingers left it at. */
  const asked = useRefT(null);
  useEfT(() => { if (asked.current !== zoom) setStretch(1); }, [zoom]);

  /* The room the viewport needs, in days — the whole width now, since the days
     run under the name column too. */
  const fill = port.host > 0
    ? Math.ceil(port.host / window.mdTimelinePixelsPerDay(zoom, stretch)) : 0;

  const axis = useMeT(() => window.mdTimelineAxis({
    ...window.mdTimelineWindow(laneRows, todayKey, zoom, fill, grown, stretch), zoom, stretch, rtl,
  }), [laneRows, todayKey, zoom, stretch, rtl, fill, grown]);

  const months = useMeT(() => window.mdTimelineMonths(axis), [axis]);
  const today = useMeT(() => window.mdTimelineToday(axis, todayKey), [axis, todayKey]);
  /* Two years of days is two years of ticks, and at day zoom that is a tick
     every 56px for 41,000 pixels. Only the ones near the window get drawn —
     the same arithmetic the rows are virtualized by, in the other axis. */
  const allTicks = useMeT(() => window.mdTimelineTicks(axis), [axis]);
  const ticks = useMeT(() => {
    if (port.host <= 0) return allTicks;
    const from = port.left - TL_ROW * 4;
    const to = port.left + port.host + TL_ROW * 4;
    return allTicks.filter((tick) => tick.x >= from && tick.x <= to);
  }, [allTicks, port.left, port.host]);

  /* Which day a pointer is over. The CANVAS's own box, not the scroller's: it
     already carries the scroll offset, so one subtraction answers it at any
     scroll position and there is no second number to keep in step. */
  const dayAt = useCbT((clientX) => {
    const box = canvas.current ? canvas.current.getBoundingClientRect() : null;
    const x = clientX - (box ? box.left : 0);
    return { day: window.mdTimelineDayAt(axis, x), inside: x >= 0 && x <= axis.width };
  }, [axis]);

  const stopPan = () => { if (panTimer.current) { window.clearInterval(panTimer.current); panTimer.current = null; } };

  /* The canvas follows a pointer that has reached the rim — otherwise a span
     can only ever be drawn as far as the window happens to be wide. Reduced
     motion turns it off entirely: this is the one animation on the screen that
     moves the whole world under you. */
  const pan = useCbT((clientX) => {
    if (tlReducedMotion()) return;
    const el = scroller.current; if (!el) return;
    const box = el.getBoundingClientRect();
    if (clientX < box.left + TL_EDGE) el.scrollLeft -= TL_PAN;
    else if (clientX > box.right - TL_EDGE) el.scrollLeft += TL_PAN;
  }, []);

  /* THE CANVAS RUNS THE WHOLE WIDTH AND THE COLUMN SITS ON TOP OF IT, so a
     scroll position and a canvas x ARE the same number — and the part of the
     canvas somebody can see is the window minus the column: `[scrollLeft +
     rail, scrollLeft + clientWidth]`, mirrored when the column is on the right.
     Getting this wrong hides a bar behind the names and calls it visible, or
     puts an off-screen stub under the column where nobody can read it. */
  const view = useMeT(() => (rtl
    ? { from: port.left, to: port.left + Math.max(0, port.host - rail) }
    : { from: port.left + rail, to: port.left + port.host }), [port.left, port.host, rail, rtl]);

  const scrollTo = (x) => {
    const el = scroller.current; if (!el) return;
    const room = Math.max(0, el.clientWidth - rail);
    el.scrollLeft = Math.max(0, x - (rtl ? 0 : rail) - room / 2);
    if (ruler.current) ruler.current.style.transform = `translateX(${-el.scrollLeft}px)`;
    readPort(el);
  };
  const scrollToToday = () => { if (today) scrollTo(today.x); };

  /* Every row is exactly TL_ROW tall, so the window a scroll position implies is
     arithmetic rather than measurement — no virtualizer, no re-measuring, no
     estimated sizes to be wrong about. A database can hand this component two
     thousand rows, and mounting two thousand of anything is the defect the
     table and the gallery already fixed.

     Before the first measurement (and in a test environment, where clientHeight
     is 0) a screenful is drawn anyway: a surface nobody has scrolled yet still
     has to show something. */
  const band = useMeT(() => {
    if (port.height <= 0) return { from: 0, to: Math.min(lanes.length, 24) };
    return { from: Math.max(0, Math.floor(port.top / TL_ROW) - 4),
      to: Math.min(lanes.length, Math.ceil((port.top + port.height) / TL_ROW) + 4) };
  }, [port.top, port.height, lanes.length]);
  const stack = lanes.length * TL_ROW;
  const shown = lanes.slice(band.from, band.to);

  /* The band and the ruler are OUTSIDE the scroller — they must not scroll
     vertically with the rows — so they are moved by hand to match it. The
     viewport is remembered too, because a row whose bar is off to the right
     gets a stub pointing at it, and a stub cannot know it is needed without
     knowing what is on screen. */
  const readPort = (el) => {
    if (el) setPort({ left: el.scrollLeft, host: el.clientWidth,
      top: el.scrollTop, height: el.clientHeight });
  };
  /* ── a scroll that reaches the end asks for more window ──────────────────
     Half a year at a time, on whichever side was reached. Which SIDE of the
     window a screen edge belongs to depends on direction — a mirrored axis puts
     the past on the right — but the compensation does not: growth at the LEFT
     edge prepends pixels, so every existing day slides right by exactly what
     was added, and the scroll has to be pushed back by the same amount or the
     canvas jumps out from under the hand. Growth at the right edge appends, and
     moves nothing.

     One growth per re-render: several scroll events land before React has
     redrawn, and each of them would otherwise ask for its own half-year. */
  const grow = (el) => {
    if (!el || growing.current || el.clientWidth <= 0) return;
    const atLeft = el.scrollLeft <= TL_GROW_EDGE;
    /* Only where the far end is actually KNOWN. An unmeasured scroller reports
       a scrollWidth of zero, and "the end is behind you" would be true forever
       — growing the window on every scroll event for as long as anybody looks
       at it. */
    const atRight = el.scrollWidth > el.clientWidth
      && el.scrollLeft + el.clientWidth >= el.scrollWidth - TL_GROW_EDGE;
    if (!atLeft && !atRight) return;
    const side = atLeft === !rtl ? 'behind' : 'ahead';
    growing.current = true;
    if (atLeft) nudged.current += TL_GROW * window.mdTimelinePixelsPerDay(zoom, stretch);
    setGrown((was) => ({ ...was, [side]: was[side] + TL_GROW }));
  };

  const onScroll = (event) => {
    if (ruler.current) ruler.current.style.transform = `translateX(${-event.currentTarget.scrollLeft}px)`;
    readPort(event.currentTarget);
    grow(event.currentTarget);
  };

  /* And the compensation, once the wider axis is on the screen: the days that
     were prepended have pushed everything right, so the scroll goes right with
     them and the view has not moved at all. */
  useEfT(() => {
    growing.current = false;
    const el = scroller.current;
    if (!nudged.current || !el) return;
    el.scrollLeft += nudged.current;
    nudged.current = 0;
    if (ruler.current) ruler.current.style.transform = `translateX(${-el.scrollLeft}px)`;
    readPort(el);
  }, [axis && axis.width, axis && axis.from]);

  /* ── the pinch ───────────────────────────────────────────────────────────
     THE DAY UNDER THE FINGERS DOES NOT MOVE. That is the whole gesture: a zoom
     that keeps the scroll position instead sends whatever you were looking at
     off the side of the screen, and you spend the zoom you asked for finding
     the place you already had.

     It cannot be done in one step, because the scale change and the scroll that
     answers it are two different frames: the axis is wider only after React has
     drawn it, and the day's new pixel does not exist until then. So the pinch
     remembers the day it was over and where on the screen that was, and the
     scroll is put back under it in an effect — the same shape as the window's
     own growth compensation above, for the same reason. */
  const anchor = useRefT(null);
  const openedAt = useRefT(null);
  const zoomBy = useCbT((factor, clientX) => {
    const el = scroller.current;
    if (!axis || !factor || !Number.isFinite(factor) || factor <= 0) return;
    const next = window.mdTimelineZoomPinch(zoom, stretch, factor);
    /* Without a caller to tell, a pinch stays inside the name it was given
       rather than silently drawing at a scale the lit chip disagrees with. */
    const name = onZoom ? next.zoom : zoom;
    const scale = onZoom ? next.stretch
      : window.mdTimelinePixelsPerDay(next.zoom, next.stretch)
        / window.mdTimelinePixelsPerDay(zoom, 1);
    if (name === zoom && Math.abs(scale - stretch) < 0.0005) return;
    if (el) {
      const box = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
      const host = (box && box.width) || el.clientWidth || 0;
      const raw = (clientX == null ? host / 2 : clientX - (box ? box.left : 0));
      const at = host > 0 ? Math.min(Math.max(raw, 0), host) : raw;
      const day = window.mdTimelineDayAt(axis, el.scrollLeft + at);
      if (day) {
        anchor.current = { day, at,
          frac: (el.scrollLeft + at - window.mdTimelineX(axis, day)) / axis.ppd };
      }
    }
    setStretch(scale);
    if (name !== zoom && onZoom) {
      /* The opening scroll watches the zoom and would yank the canvas back to
         today the moment the name changed. A zoom the hand is already holding
         has been opened. */
      asked.current = name;
      openedAt.current = name;
      onZoom(name);
    }
  }, [axis, zoom, stretch, onZoom]);

  useEfT(() => {
    const el = scroller.current;
    const held = anchor.current;
    if (!held || !el || !axis) return;
    anchor.current = null;
    const x = window.mdTimelineX(axis, held.day) + held.frac * axis.ppd;
    el.scrollLeft = Math.max(0, x - held.at);
    if (ruler.current) ruler.current.style.transform = `translateX(${-el.scrollLeft}px)`;
    readPort(el);
  }, [axis && axis.ppd, axis && axis.from, axis && axis.width]);

  /* A trackpad pinch arrives as a wheel with the control key held — that is
     what the platform does with two fingers, in every browser. It has to be a
     listener of our own: React's own wheel listener is passive, and a passive
     listener cannot stop the browser zooming the whole page instead. */
  useEfT(() => {
    const wheeled = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      if (event.cancelable) event.preventDefault();
      zoomBy(Math.exp(-event.deltaY / 120), event.clientX);
    };
    /* On the ruler as well as the days: the strip is where a hand goes to move
       around the timeline, and a pinch that dies over the dates is a pinch
       aimed at exactly the thing being zoomed. */
    const on = [scroller.current, head.current].filter((el) => el && el.addEventListener);
    for (const el of on) el.addEventListener('wheel', wheeled, { passive: false });
    return () => { for (const el of on) el.removeEventListener('wheel', wheeled); };
  }, [zoomBy]);

  /* And two actual fingers, for the iPad this was designed on. A mouse cannot
     produce a second pointer, so there is nothing to filter: the moment a
     surface has two of them, the gesture is a pinch. */
  const fingers = useRefT(null);
  if (!fingers.current) fingers.current = new Map();
  const spread = useRefT(0);
  const apart = (held) => {
    const [a, b] = [...held.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  const middle = (held) => {
    const [a, b] = [...held.values()];
    return (a.x + b.x) / 2;
  };
  const fingerDown = (event) => {
    const held = fingers.current;
    if (event.pointerId == null) return;
    held.set(event.pointerId, { x: event.clientX, y: event.clientY || 0 });
    const lift = (done) => {
      if (done.pointerId != null && done.pointerId !== event.pointerId) return;
      held.delete(event.pointerId);
      spread.current = 0;
      window.removeEventListener('pointerup', lift);
      window.removeEventListener('pointercancel', lift);
    };
    window.addEventListener('pointerup', lift);
    window.addEventListener('pointercancel', lift);
    if (held.size !== 2) return;
    /* A second finger says the first one was not drawing a span, or pulling
       the ruler, after all. Half a pinch is nobody's decision, so the draft
       goes without a write and the pull is simply let go of. */
    if (gesture.current) { gesture.current = null; stopPan(); setLive(null); }
    if (ruling.current) ruling.current();
    spread.current = apart(held);
  };
  const fingerMove = (event) => {
    const held = fingers.current;
    if (held.size < 2 || event.pointerId == null || !held.has(event.pointerId)) return;
    held.set(event.pointerId, { x: event.clientX, y: event.clientY || 0 });
    const now = apart(held);
    if (now <= 0) return;
    const was = spread.current;
    spread.current = now;
    if (was > 0) zoomBy(now / was, middle(held));
  };

  /* ── the ruler is a handle ───────────────────────────────────────────────
     A timeline is scrolled sideways more than it is anything else, and until
     now the only ways to do it were a scrollbar a finger cannot find and a
     two-finger trackpad swipe. A drag on the canvas cannot be it: there, a drag
     DRAWS a span, which is the right answer there and leaves touch with
     nothing. So the date strip is the handle — grab it anywhere and the days
     come with the hand.

     Incremental, not `start - travelled`: the window GROWS at its own edges
     while you drag into them, and a growth at the left prepends pixels and
     pushes the scroll along. A drag holding onto where it started would undo
     that push on its next move and the days would jump. Each move moves the
     scroll by the distance since the LAST one, so anything else moving the
     canvas underneath is simply respected. */
  const [ruled, setRuled] = useStT(false);
  const ruling = useRefT(null);
  const glide = useRefT(0);
  const nowMs = () => (typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now());
  const stopGlide = () => {
    if (!glide.current) return;
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(glide.current);
    glide.current = 0;
  };
  /* Setting `scrollLeft` by hand does not always tell the ruler — a programmatic
     scroll fires the handler in a browser, and never in a test — so the two
     things a scroll keeps in step are kept in step here too. */
  const scrolledTo = (el, x) => {
    el.scrollLeft = Math.max(0, x);
    if (ruler.current) ruler.current.style.transform = `translateX(${-el.scrollLeft}px)`;
    readPort(el);
    grow(el);
  };
  /* A flick coasts. A surface two years wide that stops dead the moment the
     finger lifts is a surface you have to scroll ten times to cross once —
     and reduced motion says no, the same as the edge-pan does. */
  const flick = (speed) => {
    const el = scroller.current;
    if (!el || tlReducedMotion() || typeof requestAnimationFrame !== 'function') return;
    if (!Number.isFinite(speed) || Math.abs(speed) < TL_FLICK_MIN) return;
    let rate = Math.max(-TL_FLICK_MAX, Math.min(TL_FLICK_MAX, speed));
    let at = el.scrollLeft;
    let last = nowMs();
    const step = () => {
      glide.current = 0;
      const here = scroller.current;
      if (!here) return;
      const now = nowMs();
      const span = Math.min(48, Math.max(1, now - last));
      last = now;
      const next = at + rate * span;
      at = Math.max(0, next);
      scrolledTo(here, at);
      /* Decayed per millisecond rather than per frame, so a slow frame slows
         the throw by as much as it delayed it. */
      rate *= TL_FLICK_DECAY ** (span / 16);
      if (Math.abs(rate) < TL_FLICK_STOP || (at <= 0 && rate < 0)) return;
      glide.current = requestAnimationFrame(step);
    };
    glide.current = requestAnimationFrame(step);
  };
  const grabRuler = (event) => {
    /* The column's own edge lives in this strip and means something else. */
    if (event.target && event.target.closest && event.target.closest('[data-md-rail-grip]')) return;
    const el = scroller.current;
    if (!el || fingers.current.size > 1) return;
    stopGlide();
    if (event.preventDefault) event.preventDefault();
    let last = { x: event.clientX, t: nowMs() };
    let rate = 0;
    setRuled(true);
    const move = (moved) => {
      const here = scroller.current;
      if (!here) return;
      const travelled = moved.clientX - last.x;
      scrolledTo(here, here.scrollLeft - travelled);
      const now = nowMs();
      const span = now - last.t;
      /* Smoothed, because the last event before a release is often a tiny one
         and a throw taken from it alone reads as a hand that stopped. */
      if (span > 0) rate = rate ? rate * 0.4 + (-travelled / span) * 0.6 : -travelled / span;
      last = { x: moved.clientX, t: now };
    };
    const up = () => { drop(); flick(rate); };
    const drop = () => {
      ruling.current = null;
      setRuled(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', drop);
    };
    ruling.current = drop;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', drop);
  };
  /* The gesture's keyboard twin: a week of days per press, and Home for the
     same place the Today button goes. Left and right mean what they point at
     on the screen — the scroller is ltr whichever way the days run. */
  const rulerKey = (event) => {
    if (event.target && event.target.closest && event.target.closest('[data-md-rail-grip]')) return;
    const el = scroller.current;
    if (!el || !axis) return;
    if (event.key === 'Home') {
      event.preventDefault(); stopGlide(); scrollToToday(); return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    stopGlide();
    const step = 7 * axis.ppd;
    scrolledTo(el, el.scrollLeft + (event.key === 'ArrowLeft' ? -step : step));
  };
  useEfT(() => stopGlide, []);

  /* ── the name column's own edge ──────────────────────────────────────────
     A pointer drag on the header cell's edge, and arrow keys for the hand that
     never touches a mouse. The same pointer-events grammar as the bars — one
     API for mouse, pen and finger — and the same live-draft shape: the column
     follows the hand, and there is nothing to write on release because the
     width is the surface's own business, not the record's. */
  const [railGrip, setRailGrip] = useStT(false);
  const grabRail = (event) => {
    event.preventDefault();
    const from = rail;
    const startX = event.clientX;
    setRailGrip(true);
    const move = (moved) => setRailHeld(from + (moved.clientX - startX) * (rtl ? -1 : 1));
    const up = () => {
      setRailGrip(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  const railKey = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const step = (event.key === 'ArrowLeft' ? -1 : 1) * (rtl ? -1 : 1);
    setRailHeld(rail + step * TL_RAIL_STEP);
  };

  const begin = (kind, row, clientX, edge) => {
    if (readOnly || !onWrite) return;
    const here = dayAt(clientX);
    if (!here.day) return;
    gesture.current = { kind, id: row.id, edge, grab: here.day, clientX,
      from: { start: row.start || null, target: row.target || null }, moved: false };
    setLive({ id: row.id, start: row.start || null, target: row.target || null, day: here.day });
    panTimer.current = window.setInterval(() => {
      if (gesture.current) pan(gesture.current.clientX);
    }, 40);
  };

  /* What the draft looks like at this pointer position. Pure, so the same
     answer is used for the drawing and for the write. */
  const shapeAt = (held, day) => {
    if (held.kind === 'draw') {
      return held.grab <= day ? { start: held.grab, target: day } : { start: day, target: held.grab };
    }
    if (held.kind === 'resize') return window.mdTimelineResize(held.from, held.edge, day);
    return window.mdTimelineMove(held.from, window.mdTimelineDayDiff(held.grab, day));
  };

  useEfT(() => {
    if (!live) return undefined;
    const move = (event) => {
      const held = gesture.current; if (!held) return;
      held.clientX = event.clientX;
      const here = dayAt(event.clientX);
      pan(event.clientX);
      if (!here.day) return;
      if (here.day !== held.grab) held.moved = true;
      setLive({ id: held.id, day: here.day, ...shapeAt(held, here.day) });
    };
    const up = (event) => {
      const held = gesture.current; gesture.current = null; stopPan(); setLive(null);
      if (!held) return;
      const here = dayAt(event.clientX);
      /* A pointer let go off the canvas chose nothing; a pointer that never
         travelled asked to OPEN the thing it was resting on, which is what a
         click is. Neither writes. */
      if (!here.inside || !here.day) return;
      if (!held.moved && here.day === held.grab) { if (onOpen) onOpen(held.id); return; }
      const next = shapeAt(held, here.day);
      if (next.start === held.from.start && next.target === held.from.target) return;
      if (onWrite) onWrite(held.id, next);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [live && live.id, axis]);

  useEfT(() => stopPan, []);

  /* A timeline opens on the present. The window is as wide as the furthest
     target anybody has set — two years out, in a real account — and scroll
     position zero is that window's far left edge, which is a screen full of
     last summer. Re-run on a zoom change, because the pixel today sits at has
     just moved. */
  useEfT(() => {
    /* …unless the present is nowhere near the work. A database of last year's
       events has a window that reaches today (the engine guarantees it, so the
       Today button always has somewhere to go) — but opening ON today would be
       a screen of empty space with every bar behind you. Open where the days
       are, and let the stubs point the way back.

       Once per zoom: the window also grows when the VIEWPORT does, and a
       resize is not a new opening — yanking the scroll back to today because
       somebody widened their window would be the surface stealing the wheel. */
    if (openedAt.current === zoom) return;
    openedAt.current = zoom;
    const days = [];
    for (const row of laneRows) { if (row.start) days.push(row.start); if (row.target) days.push(row.target); }
    const first = days.length ? days.reduce((a, b) => (a < b ? a : b)) : null;
    const last = days.length ? days.reduce((a, b) => (a > b ? a : b)) : null;
    if (first && todayKey && (todayKey < first || todayKey > last)) scrollTo(window.mdTimelineX(axis, first));
    else scrollToToday();
  }, [zoom, axis && axis.width]);

  /* The first measurement, before anybody has scrolled. */
  useEfT(() => { readPort(scroller.current); }, []);

  /* And every one after that the window causes rather than the scrollbar:
     resize the window and the timeline notices without being scrolled.
     Guarded — happy-dom and older embedders have no ResizeObserver, and the
     scroll path keeps the numbers honest there. */
  useEfT(() => {
    if (typeof ResizeObserver === 'undefined' || !scroller.current) return undefined;
    const watcher = new ResizeObserver(() => readPort(scroller.current));
    watcher.observe(scroller.current);
    return () => watcher.disconnect();
  }, []);

  /* ── fitting the column to the names in it ───────────────────────────────
     THE NAMES ARE MEASURED IN A CANVAS, NOT IN THE DOM. Only the rows inside
     the virtualization band are mounted, and a column fitted to those would
     change width as you scrolled past a longer name — the frozen thing on the
     screen moving because of something off it. A 2D context asked with the
     row's OWN computed font answers for every name in the list, mounted or not.

     The chips beside the name cannot be measured that way — they are whatever
     the caller hangs there, and this component has no business knowing. Those
     are measured where they are, at their widest, which is the case the column
     has to survive anyway.

     Fonts land after the first paint, so the measurement is taken again when
     they do: fitting Inter's text with the fallback's metrics is a column that
     is right about the wrong typeface. */
  const railBox = useRefT(null);
  const fitCtx = useRefT(null);
  const namesKey = useMeT(() => laneRows.map((row) => row.name || '').join(''), [laneRows]);
  const fitRail = useCbT(() => {
    const box = railBox.current;
    const probe = box && box.querySelector('[data-md-rail-open]');
    if (!probe || typeof document === 'undefined') return;
    const style = window.getComputedStyle ? window.getComputedStyle(probe) : null;
    if (!style) return;
    const font = style.font && style.font.trim()
      ? style.font
      : `${style.fontWeight || 570} ${style.fontSize || '12.5px'} ${style.fontFamily || 'sans-serif'}`;
    if (!fitCtx.current) {
      const scratch = document.createElement('canvas');
      fitCtx.current = scratch && scratch.getContext ? scratch.getContext('2d') : null;
    }
    const ctx = fitCtx.current;
    if (!ctx || typeof ctx.measureText !== 'function') return;
    ctx.font = font;
    let widest = 0;
    for (const row of laneRows) {
      const size = ctx.measureText(String(row.name || ''));
      widest = Math.max(widest, (size && size.width) || 0);
    }
    let chips = 0;
    for (const node of box.querySelectorAll('[data-md-rail-chips]')) {
      chips = Math.max(chips, node.scrollWidth || 0);
    }
    /* 10 and 8 are the row's own padding, 6 the gap the chips sit behind it. */
    const total = 10 + Math.ceil(widest) + (chips ? 6 + chips : 0) + 8 + TL_FIT_PAD;
    const next = Math.max(TL_RAIL_MIN, Math.min(TL_RAIL_MAX, total));
    setRailFit((was) => (was != null && Math.abs(was - next) <= 1 ? was : next));
  }, [laneRows]);

  useEfT(() => { fitRail(); }, [namesKey, railFor, fitRail]);
  useEfT(() => {
    const fonts = typeof document !== 'undefined' ? document.fonts : null;
    if (!fonts || !fonts.ready || typeof fonts.ready.then !== 'function') return;
    fonts.ready.then(() => fitRail());
  }, [fitRail]);

  /* Every gesture's keyboard twin. A day per press, and under RTL the arrows
     mean what they point at on screen — left is LATER on a mirrored axis. */
  const nudge = (row, edge, keyName) => {
    if (readOnly || !onWrite) return false;
    const step = (keyName === 'ArrowLeft' ? -1 : 1) * (rtl ? -1 : 1);
    const from = { start: row.start || null, target: row.target || null };
    const next = edge
      ? window.mdTimelineResize(from, edge, window.mdTimelineAddDays(from[edge === 'start' ? 'start' : 'target'] || from.target || from.start, step))
      : window.mdTimelineMove(from, step);
    if (next.start === from.start && next.target === from.target) return false;
    onWrite(row.id, next);
    return true;
  };

  const onKey = (row, edge) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (onOpen) { event.preventDefault(); onOpen(row.id); }
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (nudge(row, edge, event.key)) event.preventDefault();
  };

  const spanOf = (row) => (live && live.id === row.id
    ? { start: live.start, target: live.target }
    : { start: row.start || null, target: row.target || null });

  const line = inkT(light ? 0.055 : 0.05);
  const majorLine = inkT(light ? 0.1 : 0.09);

  /* A bar that is not on screen leaves a stub at the near edge saying which way
     it went and when it is. Without it, a window two years wide reads as a
     screen full of empty rows, and the only way to find out whether a project
     has dates at all is to drag the scrollbar until something appears.

     Only where a viewport is actually known: a surface that has never been
     measured has no business claiming anything is off it.

     A bar hidden BEHIND the name column counts as off the screen, because it
     is: the column is opaque, and "you could see it if the column were not
     there" is not a thing anybody can see. */
  const stubFor = (box) => {
    if (!box || seen <= 0) return null;
    if (box.x + box.width < view.from) return 'before';
    if (box.x > view.to) return 'after';
    return null;
  };

  const track = (row, offset) => {
    const index = band.from + offset;
    const value = spanOf(row);
    const box = window.mdTimelineSpan(axis, value.start, value.target);
    const colour = row.color ? rgbaT(row.color, 0.85) : rgbaT(accent, 0.85);
    const stub = stubFor(box);
    return <div key={row.id} data-md-track={row.id}
      onPointerDown={(event) => {
        if (event.target.closest('[data-md-bar],[data-md-grip]')) return;
        begin('draw', row, event.clientX);
      }}
      style={{ position: 'absolute', insetInline: 0, top: index * TL_ROW, height: TL_ROW,
        cursor: readOnly ? 'default' : 'cell', touchAction: 'pan-y' }}>
      {stub && <button type="button" data-md-stub={stub}
        aria-label={`${row.name} is ${stub === 'before' ? 'earlier' : 'later'} — ${tlSpanLabel(value.start, value.target)}`}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => scrollTo(box.x + box.width / 2)}
        style={{ position: 'absolute', top: (TL_ROW - 22) / 2, height: 22,
          left: stub === 'before' ? view.from + 6 : view.to - 116,
          maxWidth: 110, display: 'flex', alignItems: 'center', gap: 4, border: 'none',
          borderRadius: 'var(--md-radius-control, 8px)', padding: '0 8px', cursor: 'pointer',
          font: 'inherit', fontSize: 10.5, whiteSpace: 'nowrap', overflow: 'hidden',
          background: inkT(light ? 0.05 : 0.07), color: inkT(0.5) }}>
        {stub === 'before' ? '←' : ''}{tlSpanLabel(value.start, value.target)}{stub === 'after' ? '→' : ''}
      </button>}
      {box && <>
        <span data-md-bar-label={row.id} aria-hidden="true"
          style={{ position: 'absolute', left: box.x, top: 4, maxWidth: 260, color: inkT(0.72),
            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          {row.name}</span>
        <button type="button" data-md-bar={row.id} onKeyDown={onKey(row, null)}
          aria-label={`${row.name} — ${tlSpanLabel(value.start, value.target)}`}
          onPointerDown={(event) => { event.stopPropagation(); begin('move', row, event.clientX); }}
          style={{ position: 'absolute', left: box.x, top: TL_ROW - TL_BAR - 8, width: box.width,
            height: TL_BAR, border: 'none', borderRadius: 6, padding: 0, background: colour,
            cursor: readOnly ? 'pointer' : 'grab', touchAction: 'none' }} />
        {!readOnly && resizable && ['start', 'target'].map((edge) => {
          const at = edge === 'start' ? box.x : box.x + box.width;
          return <button key={edge} type="button" data-md-grip={edge}
            aria-label={`Move the ${edge === 'start' ? 'start' : 'end'} of ${row.name}`}
            onKeyDown={onKey(row, edge)}
            onPointerDown={(event) => { event.stopPropagation();
              begin('resize', row, event.clientX, edge); }}
            style={{ position: 'absolute', left: at - TL_GRIP / 2, top: (TL_ROW - 44) / 2,
              width: TL_GRIP, height: 44, border: 'none', background: 'transparent',
              cursor: 'ew-resize', padding: 0, touchAction: 'none' }} />;
        })}
      </>}
    </div>;
  };

  if (!axis) return null;

  return <div data-md-timeline style={{ display: 'flex', flexDirection: 'column', flex: 1,
    minHeight: 0, minWidth: 0 }}>

    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none', padding: '0 10px 8px' }}>
      <button type="button" aria-label="Scroll to today" onClick={scrollToToday}
        style={{ height: 27, border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
          padding: '0 10px', cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 620,
          background: inkT(light ? 0.05 : 0.07), color: inkT(0.72) }}>Today</button>
      <span style={{ flex: 1 }} />
      {(window.MD_TIMELINE_ZOOMS || []).map((option) => <button key={option} type="button"
        aria-label={`Zoom to ${option}`} aria-pressed={option === zoom ? 'true' : undefined}
        onClick={() => { asked.current = null; setStretch(1); if (onZoom) onZoom(option); }}
        style={{ height: 27, border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
          padding: '0 9px', cursor: 'pointer', font: 'inherit', fontSize: 11.5,
          fontWeight: option === zoom ? 660 : 560,
          background: option === zoom ? rgbaT(accent, light ? 0.12 : 0.16) : 'transparent',
          color: inkT(option === zoom ? 0.85 : 0.45) }}>{TL_ZOOM_LABEL[option] || option}</button>)}
    </div>

    {/* The band and the ruler sit OUTSIDE the scroller and are moved to match
        it, so they stay put while the rows scroll under them — and they run the
        WHOLE width, over the name column too. That is the whole of what makes
        the header one ruler: scroll into the past and the months keep going
        above the names instead of stopping at the column's edge. */}
    <div ref={head} data-md-rail-head role="group" tabIndex={0}
      aria-label="Dates. Drag to scroll the timeline, or use the arrow keys."
      onPointerDown={(event) => { fingerDown(event); grabRuler(event); }}
      onPointerMove={fingerMove} onKeyDown={rulerKey}
      /* The whole strip is a handle, so it says so — and takes the touch itself
         rather than letting the page scroll out from under it. */
      style={{ position: 'relative', flex: 'none', height: TL_HEAD,
        overflow: 'hidden', borderBottom: `1px solid ${majorLine}`,
        cursor: ruled ? 'grabbing' : 'grab', touchAction: 'none',
        outline: 'none', userSelect: 'none' }}>
      {/* The column's edge, and the one place the column can be taken hold of.
          The divider IS the affordance: a hairline at rest, the accent under
          the hand, and 11px of grabbable width around a 1px line so a finger
          can find it. It carries no background of its own — a header cell that
          painted over the ruler would take back the continuity above. */}
      <button type="button" data-md-rail-grip
        aria-label="Resize the name column"
        onPointerDown={grabRail} onKeyDown={railKey}
        style={{ position: 'absolute', insetBlock: 0, width: 11, zIndex: 3,
          [rtl ? 'right' : 'left']: rail - 5,
          display: 'flex', alignItems: 'stretch', justifyContent: 'center',
          border: 'none', padding: 0, background: 'transparent',
          cursor: 'col-resize', touchAction: 'none' }}>
        <span aria-hidden="true" style={{ width: railGrip ? 2 : 1,
          background: railGrip ? rgbaT(accent, 0.85) : majorLine }} />
      </button>
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <div ref={ruler} style={{ position: 'absolute', insetBlock: 0, left: 0, width: axis.width,
          willChange: 'transform' }}>
          {/* The label rides along inside its own month rather than sitting at
              its first day: scroll into the middle of August and the word you
              need is the one that has just gone off the left edge. It stops at
              the month's own end, so it never wanders into September. */}
          {months.map((month) => <span key={month.key} data-md-month={month.key}
            style={{ position: 'absolute', left: month.x, top: 4, width: month.width,
              paddingInlineStart: 6, boxSizing: 'border-box', color: inkT(0.6), fontSize: 11,
              fontWeight: 640, whiteSpace: 'nowrap', overflow: 'hidden',
              borderInlineStart: `1px solid ${majorLine}` }}>
            <span data-md-month-label style={{ display: 'inline-block',
              transform: `translateX(${Math.min(Math.max(0, port.left - month.x),
                Math.max(0, month.width - 72))}px)` }}>{month.label}</span>
          </span>)}
          {ticks.map((tick) => <span key={tick.day} data-md-tick={tick.day}
            style={{ position: 'absolute', left: tick.x, bottom: 4, color: inkT(tick.major ? 0.5 : 0.28),
              fontSize: 9.5, paddingInlineStart: 3, fontVariantNumeric: 'tabular-nums' }}>{tick.label}</span>)}
          {/* Today wears its date on the axis. It rides the ruler, so it
              scrolls with the days it names — and it is decoration to a screen
              reader: the Today button is the accessible control, and saying
              the date twice is not saying it better. */}
          {today.visible && <span data-md-today-pill aria-hidden="true"
            style={{ position: 'absolute', left: today.x + today.width / 2, bottom: 2,
              transform: 'translateX(-50%)', padding: '2px 6px', borderRadius: 5,
              background: rgbaT(accent, 0.95), color: '#fff', fontSize: 9, fontWeight: 700,
              letterSpacing: 0.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
            {tlDayLabel(typeof todayKey === 'string' ? todayKey : '').toUpperCase()}</span>}
        </div>
      </div>
    </div>

    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
    {/* Panning yes, the browser's own pinch no: two fingers on this surface are
        the timeline's zoom, not the page's.
        The comment sits OUTSIDE the tag on purpose. verify-a11y's tag scanner
        tracks quote state to find where an opening tag really ends, and prose
        apostrophes inside a tag read as quotes: this comment left one open and
        the scanner ran 3,734 characters past the `>`, through to the rail's
        button, then blamed this div for that button's onClick — and skipped
        every tag it had swallowed. Keep tag interiors free of prose. */}
    <div ref={scroller} data-md-scroller className="md-scroll" onScroll={onScroll}
      onPointerDown={fingerDown} onPointerMove={fingerMove}
      style={{ flex: 1, minHeight: 0, overflow: 'auto', direction: 'ltr',
        touchAction: 'pan-x pan-y' }}>
      {/* ONE AXIS. The content is exactly as wide as the days are, and the
          column is the only thing in flow in it — the canvas is taken out of
          flow so it can span the whole width UNDER the names rather than
          starting where they end. */}
      <div data-md-days style={{ position: 'relative', display: 'flex', width: axis.width,
        height: stack, minHeight: '100%', justifyContent: rtl ? 'flex-end' : 'flex-start' }}>
        {/* Frozen, so the names stay readable however far the days run on —
            and OPAQUE, because the days now run behind it. */}
        <div ref={railBox} data-md-rail style={{ position: 'sticky', zIndex: 2, width: rail,
          flex: 'none', [rtl ? 'right' : 'left']: 0,
          background: light ? '#f8f9fa' : '#000',
          /* The whole edge lights up under the hand: the thing being moved is
             the column, not the 41px of divider inside the header. */
          borderInlineEnd: `1px solid ${railGrip ? rgbaT(accent, 0.85) : majorLine}`,
          height: stack, minHeight: '100%' }}>
          {shown.map((lane, offset) => (lane.head
            ? <div key={`head-${lane.head.id}`} data-md-section={lane.head.id}
              style={{ position: 'absolute', insetInline: 0, top: (band.from + offset) * TL_ROW,
                display: 'flex', alignItems: 'center', gap: 7, height: TL_ROW,
                padding: '0 10px', boxSizing: 'border-box' }}>
              {lane.head.mark}
              <span style={{ fontSize: 11.5, fontWeight: 640, color: inkT(0.6), whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis' }}>{lane.head.label}</span>
              <span style={{ fontSize: 11, color: inkT(0.35),
                fontVariantNumeric: 'tabular-nums' }}>{lane.head.count}</span>
            </div>
            : <div key={lane.row.id} data-md-rail-row={lane.row.id}
              data-md-drag-id={railDrag ? lane.row.id : undefined}
              onPointerDown={railDrag ? (event) => railDrag.start(event, lane.row.id) : undefined}
              style={{ position: 'absolute', insetInline: 0, top: (band.from + offset) * TL_ROW,
                display: 'flex', alignItems: 'center', gap: 6, height: TL_ROW,
                padding: '0 8px 0 10px', boxSizing: 'border-box',
                /* The hand at work: the held row lifts, the row it would land
                   on wears a landing line — on the edge the caller says the
                   row will actually take. The rail says where BEFORE the
                   release, the way the board and the list do. */
                opacity: railDrag && railDrag.lift === lane.row.id ? 0.45 : undefined,
                boxShadow: railDrag && railDrag.over === lane.row.id
                  ? `inset 0 ${railDrag.edge === 'after' ? '-2px' : '2px'} 0 ${rgbaT(accent, 0.85)}`
                  : undefined,
                touchAction: railDrag ? 'none' : undefined }}>
            {/* The name is a DOOR, not a caption — the one way into a project
                that has no bar to click yet. Same keyboard grammar as the bar
                buttons: a native button, Enter and Space for free. A click that
                ends a drag is the drag's, and the hand swallows it. */}
            <button type="button" data-md-rail-open={lane.row.id} dir="auto"
              onClick={() => {
                if (railDrag && railDrag.swallow && railDrag.swallow()) return;
                if (onOpen) onOpen(lane.row.id);
              }}
              style={{ flexGrow: 1, flexShrink: 1, flexBasis: 'auto', minWidth: 40,
                alignSelf: 'stretch', padding: 0, border: 'none', background: 'transparent',
                textAlign: 'start', cursor: onOpen ? 'pointer' : 'default',
                color: inkT(0.8), font: 'inherit', fontSize: 12.5, fontWeight: 570,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {lane.row.name}</button>
            {/* Whatever the caller hangs beside the name — the Workshop's live
                property chips; the database hangs nothing. At the rail's 118px
                floor the chips give way BEFORE the name: a property you cannot
                read is a smaller loss than a row you cannot tell apart. */}
            {railFor && <span data-md-rail-chips
              style={{ flexGrow: 0, flexShrink: 100, flexBasis: 'auto', minWidth: 0,
                display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
              {railFor(lane.row)}</span>}
            </div>))}
        </div>

        <div ref={canvas} data-md-canvas style={{ position: 'absolute', inset: 0 }}>
          {ticks.map((tick) => <span key={tick.day} aria-hidden="true"
            style={{ position: 'absolute', insetBlock: 0, left: tick.x, width: 1,
              background: tick.major ? majorLine : line }} />)}
          {today.visible && <span data-md-today aria-hidden="true"
            style={{ position: 'absolute', insetBlock: 0, left: today.x, width: today.width,
              background: rgbaT(accent, light ? 0.09 : 0.12),
              borderInlineStart: `1px solid ${rgbaT(accent, 0.6)}` }} />}
          {/* A header's rung stays bare on the canvas — the grid lines say
              enough, and a bar-less band under a section name reads as the
              breath between groups. */}
          {shown.map((lane, offset) => (lane.row ? track(lane.row, offset) : null))}

          {/* ── the day, said at the bar ──────────────────────────────────
              The pill rides the span it is describing: it lives on the CANVAS,
              at the bar's own middle and in the line the bar's name usually
              holds, so it travels with the drag instead of the eye travelling
              to the bottom of the screen and back for every day crossed. It
              stays a hair inside the canvas at either end, so a span dragged
              off the edge never grows the scroll. */}
          {live && live.day && (() => {
            const seat = lanes.findIndex((lane) => lane.row && lane.row.id === live.id);
            const box = window.mdTimelineSpan(axis, live.start, live.target);
            if (seat < 0 || !box) return null;
            /* Centred on the span, but never past the edges of the window
               somebody is actually looking at — a span long enough for its
               middle to have scrolled behind the name column would otherwise
               hide the one label the gesture exists to read. `view` is the
               uncovered band, the same band the off-screen stubs are placed
               in. */
            const held = box.x + box.width / 2;
            const edge = 66;
            const middle = seen > edge * 2
              ? Math.min(Math.max(held, view.from + edge), view.to - edge)
              : Math.min(Math.max(held, edge), Math.max(edge, axis.width - edge));
            return <div data-md-pill role="status"
              style={{ position: 'absolute', left: middle, top: seat * TL_ROW + 1,
                transform: 'translateX(-50%)', height: 20, display: 'flex', alignItems: 'center',
                padding: '0 8px', borderRadius: 'var(--md-radius-control, 8px)',
                background: rgbaT(accent, 0.95), color: '#fff', fontSize: 11, fontWeight: 650,
                whiteSpace: 'nowrap', pointerEvents: 'none', fontVariantNumeric: 'tabular-nums' }}>
              {tlSpanLabel(live.start, live.target)} · {tlDayLabel(live.day)}
            </div>;
          })()}
        </div>
      </div>
    </div>

    {/* Said in the middle of the room. Tucked under the axis in a corner it
        reads as a footnote; centred over the empty canvas it reads as the
        answer to the question the whole screen is asking. */}
    {!lanes.length && <div data-md-empty style={{ position: 'absolute', inset: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      padding: '0 24px', color: inkT(0.42), fontSize: 13, pointerEvents: 'none' }}>{empty}</div>}
    </div>

  </div>;
}

Object.assign(window, { MdTimeline });
